import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [catOpen, setCatOpen] = useState(false);
  const [mobileCatOpen, setMobileCatOpen] = useState(false);
  const [keyword, setKeyword] = useState('');
  const location = useLocation();
  const navigate = useNavigate();

  const isActive = (path: string) =>
    path === '/' ? location.pathname === '/' : location.pathname.startsWith(path);

  const submitSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const q = keyword.trim();
    navigate(q ? `/search?q=${encodeURIComponent(q)}` : '/search');
    setMobileOpen(false);
  };

  const closeAll = () => {
    setMobileOpen(false);
    setCatOpen(false);
    setMobileCatOpen(false);
  };

  return (
    <header className="sticky top-0 z-50 w-full border-b border-background-200/70 bg-background-50/90 backdrop-blur-md">
      <div className="w-full px-4 md:px-8">
        <div className="flex h-16 items-center justify-between gap-4">
          {/* Logo */}
          <Link to="/" onClick={closeAll} className="flex items-center gap-2 cursor-pointer shrink-0">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-500 text-background-50">
              <i className="ri-film-line text-xl"></i>
            </span>
            <span className="font-heading text-xl font-black tracking-tight text-foreground-950">
              高清电影
            </span>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden items-center gap-1 md:flex">
            <Link
              to="/"
              className={`rounded-md px-3 py-2 text-sm font-medium transition-colors cursor-pointer ${
                isActive('/') ? 'text-primary-600' : 'text-foreground-700 hover:text-primary-600'
              }`}
            >
              首页
            </Link>

            <div
              className="relative"
              onMouseEnter={() => setCatOpen(true)}
              onMouseLeave={() => setCatOpen(false)}
            >
              <Link
                to="/category/action"
                className={`flex items-center gap-1 rounded-md px-3 py-2 text-sm font-medium transition-colors cursor-pointer ${
                  isActive('/category') ? 'text-primary-600' : 'text-foreground-700 hover:text-primary-600'
                }`}
              >
                分类
                <i className={`ri-arrow-down-s-line text-base transition-transform ${catOpen ? 'rotate-180' : ''}`}></i>
              </Link>
              {catOpen && (
                <div className="absolute left-0 top-full w-[420px] pt-3">
                  <div className="grid grid-cols-2 gap-1 rounded-lg border border-background-200 bg-background-50 p-3 shadow-sm">
                    {categories.map((c) => (
                      <Link
                        key={c.key}
                        to={`/category/${c.key}`}
                        onClick={closeAll}
                        className="flex items-center gap-2 rounded-md px-3 py-2 text-sm text-foreground-700 transition-colors hover:bg-primary-50 hover:text-primary-600 cursor-pointer"
                      >
                        <span className="flex h-7 w-7 items-center justify-center rounded-md bg-background-100 text-primary-500">
                          <i className={`${c.icon} text-base`}></i>
                        </span>
                        {c.name}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <Link
              to="/search"
              className={`rounded-md px-3 py-2 text-sm font-medium transition-colors cursor-pointer ${
                isActive('/search') ? 'text-primary-600' : 'text-foreground-700 hover:text-primary-600'
              }`}
            >
              搜索
            </Link>
            <Link
              to="/about"
              className={`rounded-md px-3 py-2 text-sm font-medium transition-colors cursor-pointer ${
                isActive('/about') ? 'text-primary-600' : 'text-foreground-700 hover:text-primary-600'
              }`}
            >
              关于
            </Link>
          </nav>

          {/* Desktop search box */}
          <form onSubmit={submitSearch} className="hidden lg:block">
            <div className="flex h-9 items-center gap-2 rounded-full border border-background-200 bg-background-100 px-3 focus-within:border-primary-400">
              <i className="ri-search-line text-base text-foreground-400"></i>
              <input
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索电影、导演、演员"
                className="w-44 bg-transparent text-sm text-foreground-800 outline-none placeholder:text-foreground-400"
              />
            </div>
          </form>

          {/* Mobile hamburger */}
          <button
            type="button"
            onClick={() => setMobileOpen((v) => !v)}
            className="flex h-10 w-10 items-center justify-center rounded-md text-foreground-800 md:hidden cursor-pointer"
            aria-label="菜单"
          >
            <i className={`${mobileOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="border-t border-background-200 bg-background-50 md:hidden">
          <div className="space-y-1 px-4 py-4">
            <form onSubmit={submitSearch} className="mb-3">
              <div className="flex h-10 items-center gap-2 rounded-md border border-background-200 bg-background-100 px-3">
                <i className="ri-search-line text-base text-foreground-400"></i>
                <input
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  placeholder="搜索电影"
                  className="w-full bg-transparent text-sm text-foreground-800 outline-none placeholder:text-foreground-400"
                />
              </div>
            </form>
            <Link
              to="/"
              onClick={closeAll}
              className="block rounded-md px-3 py-2.5 text-sm font-medium text-foreground-800 hover:bg-background-100 cursor-pointer"
            >
              首页
            </Link>
            <button
              type="button"
              onClick={() => setMobileCatOpen((v) => !v)}
              className="flex w-full items-center justify-between rounded-md px-3 py-2.5 text-sm font-medium text-foreground-800 hover:bg-background-100 cursor-pointer"
            >
              分类
              <i className={`ri-arrow-down-s-line text-lg transition-transform ${mobileCatOpen ? 'rotate-180' : ''}`}></i>
            </button>
            {mobileCatOpen && (
              <div className="grid grid-cols-2 gap-1 px-1 pb-1">
                {categories.map((c) => (
                  <Link
                    key={c.key}
                    to={`/category/${c.key}`}
                    onClick={closeAll}
                    className="rounded-md px-3 py-2 text-sm text-foreground-700 hover:bg-primary-50 cursor-pointer"
                  >
                    {c.name}
                  </Link>
                ))}
              </div>
            )}
            <Link
              to="/search"
              onClick={closeAll}
              className="block rounded-md px-3 py-2.5 text-sm font-medium text-foreground-800 hover:bg-background-100 cursor-pointer"
            >
              搜索
            </Link>
            <Link
              to="/about"
              onClick={closeAll}
              className="block rounded-md px-3 py-2.5 text-sm font-medium text-foreground-800 hover:bg-background-100 cursor-pointer"
            >
              关于
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}